'use client';
import { useState } from 'react';
import Slider from 'react-slick';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { LeftArrow, RightArrow } from '../carousels';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import FeatureCarouselCard, { FeatureCardProps } from './_FeatureCarouselCard';
import { BackgroundStyleEnum } from '@repo/ui/src/enums/shared-enums';
import { SliderHeadings, BottomContent, Divider, CarouselWrapper } from './styles';
import Section from '@repo/ui/src/components/Section';

interface FeatureCarouselProps {
  cards: Array<FeatureCardProps>;
  hideNavigationTabs?: boolean;
  background: string;
  lightArrows?: boolean;
  _testID?: string;
}

const FeatureCarousel = ({
  cards,
  hideNavigationTabs,
  background,
  _testID,
}: FeatureCarouselProps) => {
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion)', { noSsr: true });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lightArrows =
    background === BackgroundStyleEnum.Black ||
    background === BackgroundStyleEnum.TextureDark ||
    background === BackgroundStyleEnum.DarkGray;

  const topSettings = {
    accessibility: true,
    dots: true,
    infinite: true,
    centerMode: true,
    centerPadding: '180px',
    speed: reduceMotion ? 0 : 500,
    slidesToShow: 1,
    arrows: true,
    slidesToScroll: 1,
    focusOnSelect: true,
    nextArrow: <RightArrow onImage aria-label="Next slide" role="button" arrowSize="40" />,
    prevArrow: <LeftArrow onImage aria-label="Previous slide" role="button" arrowSize="40" />,
    responsive: [
      {
        breakpoint: 960,
        settings: {
          centerPadding: '100px',
          arrows: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          centerPadding: '25px',
          arrows: false,
        },
      },
    ],
    appendDots: (dots: boolean) => (
      <div>{(isMobile || !hideNavigationTabs) && <ul> {dots} </ul>}</div>
    ),
    customPaging: (i: number) => (
      <button
        aria-current={selectedCardIndex === i}
        aria-label={`Go to slide ${i + 1} of ${cards.length}: ${cards[i].toutContent?.heading || 'Slide content'}`}
        onClick={() => setSelectedCardIndex(i)}
      >
        <Divider />
        <SliderHeadings align="left" variant="h6">
          {cards[i].toutContent?.heading}
        </SliderHeadings>
        <span className="tw-absolute tw-w-px tw-h-px tw-p-0 tw-m-[-1px] tw-overflow-hidden tw-clip-rect-0 tw-whitespace-nowrap tw-border-0">
          {cards[i].toutContent?.heading || `Slide ${i + 1} content`}
        </span>
      </button>
    ),
  };

  return (
    <CarouselWrapper
      role="region"
      aria-label={`feature carousel ${_testID ? _testID : ''}`}
      data-test-id={`feature_carousel_${_testID}`}
      className="quote-carousel"
    >
      <span className="tw-absolute tw-w-px tw-h-px tw-p-0 tw-m-[-1px] tw-overflow-hidden tw-clip-rect-0 tw-whitespace-nowrap tw-border-0">
        This is a carousel with {cards.length} slides. Use the previous and next buttons to navigate
        between slides, or use the tab key to access the slide navigation tabs. Each slide can be
        selected directly using the tabs below the carousel.
      </span>
      <Section
        className="tw-w-full tw-overflow-hidden"
        white={!background || background === BackgroundStyleEnum.White}
        dark={background === BackgroundStyleEnum.TextureDark}
        black={background === BackgroundStyleEnum.Black}
        darkGray={background === BackgroundStyleEnum.DarkGray}
        texture={
          background === BackgroundStyleEnum.TextureLight ||
          background === BackgroundStyleEnum.TextureDark
        }
        gutterY
      >
        <Slider {...topSettings}>
          {cards.map((card, i) => (
            <FeatureCarouselCard
              key={card.toutContent?.heading || i}
              {...card}
              alt={card.toutContent?.heading || `slide ${i + 1}`}
              isActive={i === selectedCardIndex}
              index={i}
              totalSlides={cards.length}
            />
          ))}
        </Slider>
        <BottomContent className={`${lightArrows ? 'dark-mode' : ''}`}>
          <ToutContent
            divider
            lineClamp={4}
            align="left"
            className="tw-flex tw-flex-col tw-h-full tw-justify-end"
            {...cards[selectedCardIndex || 0].toutContent}
            headingStyleVariant="h3"
          />
        </BottomContent>
      </Section>
    </CarouselWrapper>
  );
};

export default FeatureCarousel;
